/**
 * THE THREE STATES A PANEL CAN BE IN THAT ARE NOT "HERE IS THE ANSWER".
 *
 * Each is a designed state, not an absence:
 *
 *   SKELETON  shaped like the content, so nothing jumps when the data lands.
 *   EMPTY     a sentence saying WHY there is nothing, in the panel's own words.
 *             "A quiet log and a quiet building are different things" is a
 *             different claim from "no data", and only one of them is true.
 *   ERROR     what failed, and a retry for THIS panel — never a reload of the
 *             whole screen to recover from one 500.
 *
 * A CHART WITH NO DATA MUST NEVER RENDER AS AN EMPTY GRID. An axis with no marks
 * reads as zero everywhere, which is a claim about the building; the empty state
 * is a claim about the record, which is the honest one.
 */
import React from 'react';
import { AppButton } from '../../../../button/app-button';
import {
  EmptyBar,
  EmptyGlyph,
  FailureBox,
  GhostButton,
  Skeleton,
  SkeletonStack,
  StateBox,
  StateText,
  StateTitle,
} from './chart.styled';

export interface EmptyReason {
  title: string;
  text: string;
  /**
   * SOMETHING TO PRESS, when the emptiness is the reader's own filter.
   *
   * Optional on purpose. "Nothing happened in this period" has no action and
   * should not pretend to; "no row matches these six chips" has an obvious one
   * and used to leave the reader to find it themselves.
   */
  action?: { label: string; onPress: () => void };
}

export const ChartSkeleton: React.FC<{ height?: number }> = ({ height = 180 }) => (
  <SkeletonStack aria-hidden="true" data-testid="chart-skeleton">
    <Skeleton h={12} w="40%" />
    <Skeleton h={height} />
    <Skeleton h={12} w="70%" />
  </SkeletonStack>
);

export const ChartEmpty: React.FC<EmptyReason> = ({ title, text, action }) => (
  <StateBox data-testid="panel-empty">
    <EmptyGlyph aria-hidden="true">
      <EmptyBar h={14} />
      <EmptyBar h={26} />
      <EmptyBar h={18} />
      <EmptyBar h={32} />
      <EmptyBar h={20} />
    </EmptyGlyph>
    <StateTitle>{title}</StateTitle>
    <StateText>{text}</StateText>
    {action ? (
      <AppButton
        variant="secondary"
        size="compact"
        data-testid="panel-empty-action"
        onClick={action.onPress}
      >
        {action.label}
      </AppButton>
    ) : null}
  </StateBox>
);

export const ChartError: React.FC<{
  title?: string;
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
}> = ({ title = 'This panel could not be loaded', message, onRetry, retryLabel }) => (
  <FailureBox role="alert" data-testid="panel-error">
    <StateTitle>{title}</StateTitle>
    <StateText>{message}</StateText>
    {onRetry ? (
      <GhostButton type="button" onClick={onRetry}>
        {retryLabel || 'Try this panel again'}
      </GhostButton>
    ) : null}
  </FailureBox>
);

/**
 * A SUB-SECTION THAT IS EMPTY WHILE ITS PANEL IS NOT.
 *
 * The dwell panel is the reason this exists. Its distribution is bounded by the
 * window and its ageing worklist is as-of-now, so "nothing closed in this
 * period" and "nothing is waiting" are INDEPENDENT states — and on real data the
 * first is often true while the second is not. Blanking the whole panel on
 * either one would hide a real answer behind an empty state.
 */
export const SectionEmpty: React.FC<EmptyReason> = ({ title, text }) => (
  <StateBox data-testid="section-empty" style={{ minHeight: 120 }}>
    <StateTitle>{title}</StateTitle>
    <StateText>{text}</StateText>
  </StateBox>
);

/**
 * A PANEL THIS MEASURE CANNOT HONESTLY FILL.
 *
 * "Parcels on the shelf per hour of the day" is not a question, and the server
 * would answer it by silently regrouping to `building` and saying so in a note.
 * That is the right server behaviour and the wrong screen: a panel captioned
 * "Hour of day" showing a building split is a chart with the wrong title, which
 * is worse than an empty one. So the panel is not requested, and it says which
 * measure it would need.
 */
export const ChartUnavailable: React.FC<{
  measureLabel: string;
  dimension: string;
}> = ({ measureLabel, dimension }) => (
  <StateBox data-testid="panel-unavailable">
    <StateTitle>
      {measureLabel
        ? `"${measureLabel}" has no meaningful split by ${dimension.toLowerCase()}`
        : 'Not available for this measure'}
    </StateTitle>
    <StateText>
      Pick a measure that is counted per record — log entries, arrivals, incidents
      or people admitted — to see this panel.
    </StateText>
  </StateBox>
);
